import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-accordion>` says about itself.
 *
 * The distinction this piece is most often on the wrong side of is with DetailSection. A detail
 * section is ONE titled block that may fold itself; an accordion is a RUN of sections that belong to
 * one another — opening one can close the rest, a person's choices can be remembered as a set, and the
 * keyboard moves between their headers. Eight detail sections with `collapsible` set are eight things
 * that each fold; an accordion of eight is one thing that is read a part at a time.
 *
 * ON THE SECTIONS. A section is not a component, and there is no `caos-accordion-section` to place.
 * The sections are the `sections` input, and what a section holds is placed inside the accordion with
 * `slot` naming that section's `value`. Every example below is written that way, because an example
 * that filled a section any other way would be showing something a page cannot store.
 */
const docs: ComponentDocs = {
  aliases: ['collapsible sections', 'expandable sections', 'disclosure', 'expander', 'fold'],

  answers: [
    'make a long record page readable a part at a time',
    'collapse the sections of a record page',
    'show one section at a time and close the rest',
    'remember which sections somebody keeps open',
    'put sections inside a section',
  ],

  // The same surfaces a detail section stands on: it organises the body of a record, or of a page
  // inside an app.
  placements: ['recordPage', 'appPage'],

  inputs: [
    {
      name: 'sections',
      // No attribute: a list of sections cannot be written in one.
      type: 'json',
      description:
        'The sections, as { value, label, open?, hidden? } objects, in the order they are drawn. value ' +
        'names the section and is the slot its content is placed into; label is the words on its ' +
        'header. The sections marked open start open, and when no section says either way the first ' +
        'one does. A hidden section is left out, with its content. A value that repeats an earlier one ' +
        'is dropped.',
    },
    {
      name: 'multiple',
      attribute: 'multiple',
      type: 'boolean',
      description:
        'Lets several sections stay open at once. Unset, opening a section closes the one that was ' +
        'open, and when several are marked open only the first of them starts open.',
      default: false,
    },
    {
      name: 'remember',
      attribute: 'remember',
      type: 'text',
      description:
        'A name under which the sections each person opens and closes are kept for them on this ' +
        'browser, so their next visit starts where they left it. Give two accordions the same name ' +
        'only if they are the same accordion. Left unset, nothing is kept and every visit starts as ' +
        'authored.',
    },
  ],

  events: [
    {
      name: 'caos-accordion-toggle',
      when:
        'A section is about to open or close — its header is pressed, or openSection, closeSection or ' +
        'toggleSection is called. Cancelable: calling preventDefault leaves every section as it was. ' +
        'A section closed because another one opened raises nothing of its own.',
      carries: '{ value, open } — the section, and the state it is about to move to.',
    },
  ],

  state: [
    {
      name: 'open',
      type: 'string[]',
      description: 'The values of the sections open now, in the order they are drawn.',
    },
  ],

  slots: [
    {
      name: '<section value>',
      takes:
        'The content of one section: anything placed with slot set to that section’s value. Several ' +
        'pieces may name the same section and are drawn in order. Content naming no section, or a ' +
        'hidden one, is not drawn.',
    },
  ],

  tokens: [
    { token: '--caos-color-border', controls: 'The hairline between one section and the next.' },
    { token: '--caos-color-text', controls: 'The header text, and the chevron of a hovered header.' },
    { token: '--caos-color-text-muted', controls: 'The chevron at rest.' },
    { token: '--caos-font', controls: 'The typeface of the headers.' },
    { token: '--caos-space-2', controls: 'The gap between the chevron and the label, and so how far content is indented.' },
    { token: '--caos-space-3', controls: 'The space above and below a header, and below an open section’s content.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on a header reached by keyboard.' },
    { token: '--caos-radius-sm', controls: 'The corners of that ring.' },
  ],

  parts: [
    { part: 'accordion', is: 'the run of sections' },
    { part: 'section', is: 'one section, its header and its content together' },
    { part: 'header', is: 'the button that opens and closes a section' },
    { part: 'label', is: 'the words on a header' },
    { part: 'panel', is: 'the region a section’s content is drawn in' },
  ],

  methods: [
    {
      name: 'openSection',
      description:
        'Open a section, exactly as pressing its header does — the same cancelable event fires first. ' +
        'Returns false when nothing changed: the section is unknown, hidden or already open, or the ' +
        'event was cancelled.',
      args: [{ name: 'value', type: 'string', description: 'The value of the section to open.' }],
    },
    {
      name: 'closeSection',
      description: 'Close a section, the same way. Returns false when nothing changed.',
      args: [{ name: 'value', type: 'string', description: 'The value of the section to close.' }],
    },
    {
      name: 'toggleSection',
      description: 'Open a closed section or close an open one. Returns false when nothing changed.',
      args: [{ name: 'value', type: 'string', description: 'The value of the section to open or close.' }],
    },
  ],

  placing:
    'Across the width of the region it organises: a page section of one column, or inside a detail ' +
    'section’s card. It adds no spacing of its own — no margin, no outer padding, and only a hairline ' +
    'between sections — so it lines up with whatever stands beside it, and an accordion placed inside ' +
    'one of its own sections is indented by the chevron and nothing more. In a section of two columns ' +
    'it takes one of them, like any other piece.',

  whenToUse:
    'A page with more sections than anybody reads at once, where each is worth reaching but few are ' +
    'worth seeing all the time — the record page with eight sections. If a person needs to compare ' +
    'two sections side by side, keep them open as detail sections instead.',

  insteadUse: [
    {
      name: 'DetailSection',
      when: 'There is one block, or a few that have nothing to do with one another; set it collapsible if it should fold.',
    },
    {
      name: 'TabStrip',
      when: 'Only one face of the subject is worth looking at at a time, and the faces are few enough to name across a row.',
    },
  ],

  accessibility:
    'Each header is a real button carrying aria-expanded, pointing aria-controls at its section, and ' +
    'each section is a region labelled by its header, so a screen reader announces what opens and ' +
    'whether it is open. Arrow Down and Arrow Up move between headers and wrap at the ends; Home and ' +
    'End go to the first and the last. Keys pressed inside a section’s content are left alone. A ' +
    'closed section’s content is hidden, not merely out of sight, so it is out of the tab order and ' +
    'the accessibility tree. The chevron turns toward the reading direction, so it reads correctly ' +
    'right to left. What is left to the author: a label that says what the section holds.',

  examples: [
    {
      key: 'basic',
      label: 'Sections of a record',
      summary:
        'The ordinary case: the sections of a record page, one open at a time, the first open because ' +
        'no section says otherwise. Opening another closes it.',
      placement: {
        component: 'caos-accordion',
        inputs: {
          sections: [
            { value: 'details', label: 'Details' },
            { value: 'billing', label: 'Billing' },
            { value: 'shipping', label: 'Shipping' },
          ],
        },
        children: [
          { component: 'caos-key-value-row', slot: 'details', inputs: { label: 'Account', value: 'Northwind Traders' } },
          { component: 'caos-key-value-row', slot: 'details', inputs: { label: 'Owner', value: 'Dana Ruiz', 'no-border': true } },
          { component: 'caos-key-value-row', slot: 'billing', inputs: { label: 'Terms', value: 'Net 30', 'no-border': true } },
          { component: 'caos-key-value-row', slot: 'shipping', inputs: { label: 'Method', value: 'Freight', 'no-border': true } },
        ],
      },
    },
    {
      key: 'multiple-open',
      label: 'Several open at once',
      summary:
        'With multiple set, sections open and close independently, and the two the author marked open ' +
        'both start open.',
      placement: {
        component: 'caos-accordion',
        inputs: {
          multiple: true,
          sections: [
            { value: 'details', label: 'Details', open: true },
            { value: 'billing', label: 'Billing', open: true },
            { value: 'notes', label: 'Notes' },
          ],
        },
        children: [
          { component: 'caos-key-value-row', slot: 'details', inputs: { label: 'Account', value: 'Northwind Traders', 'no-border': true } },
          { component: 'caos-key-value-row', slot: 'billing', inputs: { label: 'Terms', value: 'Net 30', 'no-border': true } },
          { component: 'caos-key-value-row', slot: 'notes', inputs: { label: 'Last call', value: '2 Sep 2026', 'no-border': true } },
        ],
      },
    },
    {
      key: 'conditional-section',
      label: 'A section left out',
      summary:
        'The same sections with one marked hidden: it is not drawn, and neither is what was placed in it.',
      placement: {
        component: 'caos-accordion',
        inputs: {
          sections: [
            { value: 'details', label: 'Details' },
            { value: 'approvals', label: 'Approvals', hidden: true },
            { value: 'billing', label: 'Billing' },
          ],
        },
        children: [
          { component: 'caos-key-value-row', slot: 'details', inputs: { label: 'Account', value: 'Northwind Traders', 'no-border': true } },
          { component: 'caos-key-value-row', slot: 'approvals', inputs: { label: 'Approver', value: 'Not shown', 'no-border': true } },
          { component: 'caos-key-value-row', slot: 'billing', inputs: { label: 'Terms', value: 'Net 30', 'no-border': true } },
        ],
      },
    },
    {
      key: 'nested',
      label: 'Sections inside a section',
      summary:
        'An accordion placed in one of another accordion’s sections. The same chevron marks every ' +
        'level, and the inner one is indented by that chevron alone.',
      placement: {
        component: 'caos-accordion',
        inputs: {
          sections: [
            { value: 'addresses', label: 'Addresses' },
            { value: 'terms', label: 'Terms' },
          ],
        },
        children: [
          {
            component: 'caos-accordion',
            slot: 'addresses',
            inputs: {
              sections: [
                { value: 'billing', label: 'Billing address' },
                { value: 'shipping', label: 'Shipping address' },
              ],
            },
            children: [
              { component: 'caos-key-value-row', slot: 'billing', inputs: { label: 'City', value: 'Tacoma', 'no-border': true } },
              { component: 'caos-key-value-row', slot: 'shipping', inputs: { label: 'City', value: 'Spokane', 'no-border': true } },
            ],
          },
          { component: 'caos-key-value-row', slot: 'terms', inputs: { label: 'Payment', value: 'Net 30', 'no-border': true } },
        ],
      },
    },
    {
      key: 'in-a-section',
      label: 'Inside a section’s card',
      summary:
        'An accordion inside a detail section, which brings the card. The accordion adds no spacing of ' +
        'its own, so its headers line up with the section’s content.',
      placement: {
        component: 'caos-detail-section',
        inputs: { heading: 'Account' },
        children: [
          {
            component: 'caos-accordion',
            inputs: {
              sections: [
                { value: 'details', label: 'Details' },
                { value: 'billing', label: 'Billing' },
              ],
            },
            children: [
              { component: 'caos-key-value-row', slot: 'details', inputs: { label: 'Owner', value: 'Dana Ruiz', 'no-border': true } },
              { component: 'caos-key-value-row', slot: 'billing', inputs: { label: 'Terms', value: 'Net 30', 'no-border': true } },
            ],
          },
        ],
      },
    },
  ],
};

export default docs;
